import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { useLocalizedPath } from "../components/Header";
import { Star, Copy, ExternalLink, CheckCircle, Loader2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Google Review URL placeholder - replace with actual URL when available
const GOOGLE_REVIEW_URL = "https://g.page/r/YOUR_GOOGLE_PLACE_ID/review";

function StarRating({ value, onChange, label }) {
  return (
    <div className="space-y-2">
      <label className="text-white text-sm font-medium">{label}</label>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            className="focus:outline-none transition-transform hover:scale-110"
          >
            <Star
              className={`h-8 w-8 ${
                star <= value
                  ? "text-orange-500 fill-orange-500"
                  : "text-gray-500"
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );
}

export default function ReviewPage() {
  const { t, i18n } = useTranslation();
  const { getPath } = useLocalizedPath();
  const currentLang = i18n.language?.split('-')[0] || 'de';
  
  const [formData, setFormData] = useState({
    firstName: "",
    city: "",
    email: "",
    projectType: "",
    cleanlinessRating: 0,
    worksiteOrderRating: 0,
    qualityRating: 0,
    valueRating: 0,
    transparencyRating: 0,
    communicationRating: 0,
    recommendation: null,
    comment: "",
    photoPermission: "",
    privacyConsent: false,
    honeypot: "", // Spam protection
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedComment, setSubmittedComment] = useState("");
  const [isCopied, setIsCopied] = useState(false);
  const [error, setError] = useState("");
  
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };
  
  const handleRatingChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  const validateForm = () => {
    const required = ['firstName', 'city', 'email', 'comment', 'photoPermission'];
    const ratings = ['cleanlinessRating', 'worksiteOrderRating', 'qualityRating', 'valueRating', 'transparencyRating', 'communicationRating'];
    
    for (const field of required) {
      if (!formData[field]) {
        return t('reviews.form.validation.required');
      }
    }
    
    for (const rating of ratings) {
      if (formData[rating] < 1) {
        return t('reviews.form.validation.ratings');
      }
    }
    
    if (formData.recommendation === null) {
      return t('reviews.form.validation.recommendation');
    }
    
    if (!formData.privacyConsent) {
      return t('reviews.form.validation.privacy');
    }
    
    if (formData.comment.length < 10) {
      return t('reviews.form.validation.commentLength');
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      return t('reviews.form.validation.email');
    }
    
    return null;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/reviews`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || t('reviews.form.error'));
      }
      
      setSubmittedComment(formData.comment);
      setIsSubmitted(true);
    } catch (err) {
      setError(err.message || t('reviews.form.error'));
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(submittedComment);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 3000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };
  
  const projectTypes = [
    { value: "", label: t('reviews.form.projectTypes.select') },
    { value: "trockenbau", label: t('reviews.form.projectTypes.drywall') },
    { value: "bodenbelaege", label: t('reviews.form.projectTypes.flooring') },
    { value: "renovierung", label: t('reviews.form.projectTypes.renovation') },
    { value: "sanierung", label: t('reviews.form.projectTypes.refurbishment') },
    { value: "innenausbau", label: t('reviews.form.projectTypes.interior') },
    { value: "reparaturen", label: t('reviews.form.projectTypes.repairs') },
    { value: "sonstiges", label: t('reviews.form.projectTypes.other') },
  ];
  
  // Success view after submission
  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-[#1e2a3a] pt-24 pb-16 px-4" data-testid="review-success">
        <div className="max-w-xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-500/20 text-green-500 mb-6">
            <CheckCircle className="h-10 w-10" />
          </div>
          
          <h1 className="text-3xl font-bold text-white mb-4">
            {t('reviews.success.title')}
          </h1>
          
          <p className="text-gray-300 text-lg mb-8">
            {t('reviews.success.message')}
          </p>
          
          <Card className="bg-[#2d3a4a] border-0 mb-6">
            <CardContent className="p-6">
              <p className="text-white mb-4">
                {t('reviews.success.googlePrompt')}
              </p>
              
              <Button
                onClick={copyToClipboard}
                className="w-full bg-gray-600 hover:bg-gray-700 text-white h-12 mb-3"
                data-testid="copy-review-btn"
              >
                {isCopied ? (
                  <>
                    <CheckCircle className="mr-2 h-5 w-5" />
                    {t('reviews.success.copied')}
                  </>
                ) : (
                  <>
                    <Copy className="mr-2 h-5 w-5" />
                    {t('reviews.success.copyButton')}
                  </>
                )}
              </Button>
              
              <Button
                asChild
                className="w-full bg-orange-500 hover:bg-orange-600 text-white h-12"
                data-testid="google-review-btn"
              >
                <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noopener noreferrer">
                  <Star className="mr-2 h-5 w-5" />
                  {t('reviews.success.googleButton')}
                  <ExternalLink className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </CardContent>
          </Card>
          
          <Link
            to={getPath('home')}
            className="text-orange-400 hover:text-orange-300 underline"
          >
            {t('reviews.success.backHome')}
          </Link>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#1e2a3a] pt-24 pb-16 px-4" data-testid="review-page">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t('reviews.form.title')}
          </h1>
          <p className="text-gray-300">
            {t('reviews.form.subtitle')}
          </p>
        </div>
        
        <Card className="bg-[#2d3a4a] border-0">
          <CardContent className="p-6 md:p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Honeypot - hidden spam protection */}
              <input
                type="text"
                name="honeypot"
                value={formData.honeypot}
                onChange={handleInputChange}
                className="hidden"
                tabIndex="-1"
                autoComplete="off"
              />
              
              {/* Personal Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white text-sm font-medium mb-2">
                    {t('reviews.form.firstName')} *
                  </label>
                  <Input
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="bg-[#1e2a3a] border-gray-600 text-white"
                    required
                    data-testid="review-firstname"
                  />
                </div>
                <div>
                  <label className="block text-white text-sm font-medium mb-2">
                    {t('reviews.form.city')} *
                  </label>
                  <Input
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    className="bg-[#1e2a3a] border-gray-600 text-white"
                    required
                    data-testid="review-city"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-white text-sm font-medium mb-2">
                  {t('reviews.form.email')} *
                  <span className="text-gray-400 text-xs ml-2">({t('reviews.form.emailPrivate')})</span>
                </label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="bg-[#1e2a3a] border-gray-600 text-white"
                  required
                  data-testid="review-email"
                />
              </div>
              
              <div>
                <label className="block text-white text-sm font-medium mb-2">
                  {t('reviews.form.projectType')}
                </label>
                <select
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleInputChange}
                  className="w-full h-10 px-3 rounded-md bg-[#1e2a3a] border border-gray-600 text-white"
                  data-testid="review-project-type"
                >
                  {projectTypes.map(pt => (
                    <option key={pt.value} value={pt.value}>{pt.label}</option>
                  ))}
                </select>
              </div>
              
              {/* Ratings */}
              <div className="space-y-4">
                <h3 className="text-white font-semibold text-lg border-b border-gray-600 pb-2">
                  {t('reviews.form.ratingsTitle')}
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <StarRating
                    value={formData.cleanlinessRating}
                    onChange={(v) => handleRatingChange('cleanlinessRating', v)}
                    label={t('reviews.form.ratings.cleanliness')}
                  />
                  <StarRating
                    value={formData.worksiteOrderRating}
                    onChange={(v) => handleRatingChange('worksiteOrderRating', v)}
                    label={t('reviews.form.ratings.worksiteOrder')}
                  />
                  <StarRating
                    value={formData.qualityRating}
                    onChange={(v) => handleRatingChange('qualityRating', v)}
                    label={t('reviews.form.ratings.quality')}
                  />
                  <StarRating
                    value={formData.valueRating}
                    onChange={(v) => handleRatingChange('valueRating', v)}
                    label={t('reviews.form.ratings.value')}
                  />
                  <StarRating
                    value={formData.transparencyRating}
                    onChange={(v) => handleRatingChange('transparencyRating', v)}
                    label={t('reviews.form.ratings.transparency')}
                  />
                  <StarRating
                    value={formData.communicationRating}
                    onChange={(v) => handleRatingChange('communicationRating', v)}
                    label={t('reviews.form.ratings.communication')}
                  />
                </div>
              </div>
              
              {/* Recommendation */}
              <div>
                <label className="block text-white text-sm font-medium mb-3">
                  {t('reviews.form.recommendation')} *
                </label>
                <div className="flex gap-4">
                  <Button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, recommendation: true }))}
                    className={`flex-1 h-12 ${
                      formData.recommendation === true
                        ? "bg-green-600 hover:bg-green-700"
                        : "bg-gray-600 hover:bg-gray-700"
                    }`}
                    data-testid="review-recommend-yes"
                  >
                    {t('reviews.form.yes')}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, recommendation: false }))}
                    className={`flex-1 h-12 ${
                      formData.recommendation === false
                        ? "bg-red-600 hover:bg-red-700"
                        : "bg-gray-600 hover:bg-gray-700"
                    }`}
                    data-testid="review-recommend-no"
                  >
                    {t('reviews.form.no')}
                  </Button>
                </div>
              </div>
              
              {/* Comment */}
              <div>
                <label className="block text-white text-sm font-medium mb-2">
                  {t('reviews.form.comment')} *
                </label>
                <Textarea
                  name="comment"
                  value={formData.comment}
                  onChange={handleInputChange}
                  className="bg-[#1e2a3a] border-gray-600 text-white min-h-[150px]"
                  placeholder={t('reviews.form.commentPlaceholder')}
                  required
                  data-testid="review-comment"
                />
              </div>
              
              {/* Photo Permission */}
              <div>
                <label className="block text-white text-sm font-medium mb-3">
                  {t('reviews.form.photoPermission')} *
                </label>
                <div className="flex flex-col sm:flex-row gap-3">
                  {[
                    { value: "yes", label: t('reviews.form.photoOptions.yes') },
                    { value: "no", label: t('reviews.form.photoOptions.no') },
                    { value: "anonymous", label: t('reviews.form.photoOptions.anonymous') },
                  ].map(option => (
                    <Button
                      key={option.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, photoPermission: option.value }))}
                      className={`flex-1 h-10 text-sm ${
                        formData.photoPermission === option.value
                          ? "bg-orange-500 hover:bg-orange-600"
                          : "bg-gray-600 hover:bg-gray-700"
                      }`}
                      data-testid={`review-photo-${option.value}`}
                    >
                      {option.label}
                    </Button>
                  ))}
                </div>
              </div>
              
              {/* Privacy Consent */}
              <div className="flex items-start gap-3">
                <Checkbox
                  id="privacyConsent"
                  checked={formData.privacyConsent}
                  onCheckedChange={(checked) => 
                    setFormData(prev => ({ ...prev, privacyConsent: checked }))
                  }
                  className="mt-1 border-gray-500 data-[state=checked]:bg-orange-500"
                  data-testid="review-privacy-consent"
                />
                <label htmlFor="privacyConsent" className="text-gray-300 text-sm">
                  {t('reviews.form.privacyConsent')}{" "}
                  <Link
                    to={getPath('privacy')}
                    className="text-orange-400 hover:text-orange-300 underline"
                    target="_blank"
                  >
                    {t('reviews.form.privacyLink')}
                  </Link>
                  {" "}*
                </label>
              </div>
              
              {/* Error Message */}
              {error && (
                <div className="p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-300" data-testid="review-error">
                  {error}
                </div>
              )}
              
              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white h-14 text-lg"
                data-testid="review-submit"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    {t('reviews.form.submitting')}
                  </>
                ) : (
                  t('reviews.form.submit')
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
